import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Center,
  Heading,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useParams } from "@tanstack/react-router";
import { api } from "../../convex/_generated/api";
import {
  ReviewFormFields,
  missingRequiredReviewFields,
  type ReviewFieldValue,
} from "@/components/ReviewFormFields";
import { DetailSkeleton } from "../components/LoadingSkeletons";
import { toResponseData } from "./ActivityDetailParts";

/**
 * The one page in CeMMbase that is not behind a sign-in.
 *
 * It is mounted outside the authenticated tree, so nothing here may reach for
 * the viewer, the sidebar, or the impersonation control: an anonymous visitor
 * has no viewer to read, and anything that asked for one would either throw or
 * quietly send them to Microsoft. The only thing this page knows is the token
 * in its own URL.
 *
 * Requiring a login would make answers FEEL attributable even where they are
 * not, and for feedback the perception matters as much as the fact (model
 * §14). The price is that a determined person could respond twice, which for
 * an internal workshop form is an acceptable risk -- and the link can be
 * closed the moment it is abused.
 *
 * The form renderer is the one the review forms use. That reuse is the whole
 * argument for doing this as a route rather than as hand-written HTML in an
 * httpAction.
 */
export function FeedbackPage() {
  const { token } = useParams({ from: "/feedback/$token" });
  const form = useQuery(api.eventFeedback.getFormByToken, { token });
  const submit = useMutation(api.eventFeedback.submitFeedback);

  const [values, setValues] = useState<Record<string, ReviewFieldValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (form === undefined) {
    return <Shell><DetailSkeleton label="Loading the form" /></Shell>;
  }

  // One message for every way a link can fail to lead anywhere. Saying which
  // would make this a way of testing whether a guessed token had ever been
  // real.
  if (form === null) {
    return (
      <Shell>
        <Heading size="lg">This link does not lead anywhere</Heading>
        <Text color="fg.muted">
          The feedback form may have closed, or the link may have been copied incompletely.
        </Text>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <Heading size="lg">Thank you</Heading>
        <Text color="fg.muted">
          Your feedback on {form.title} has been recorded. It is anonymous: nothing about who
          you are was stored with it.
        </Text>
      </Shell>
    );
  }

  // Comparing days as strings is safe -- they are "YYYY-MM-DD" -- and this is
  // a courtesy, not the rule. The server checks it again on submission, which
  // is what actually closes the form.
  const closed = form.closesOn !== null && todayInVienna() > form.closesOn;

  if (form.cancelled || closed) {
    return (
      <Shell>
        <Heading size="lg">This form is closed</Heading>
        <Text color="fg.muted">
          {form.cancelled
            ? `${form.title} was cancelled.`
            : `Feedback on ${form.title} closed on ${form.closesOn}.`}
        </Text>
      </Shell>
    );
  }

  const missing = missingRequiredReviewFields(form.formSchema.fields, values);

  async function onSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      await submit({ token, responseData: toResponseData(values) });
      setDone(true);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message.replace(/^\[.*?\]\s*/, "")
          : "Something went wrong. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Shell>
      <Stack gap={1}>
        <Heading size="lg">{form.title}</Heading>
        <Text fontSize="sm" color="fg.muted">
          {form.startDay}
          {form.location === "" ? "" : ` · ${form.location}`}
        </Text>
      </Stack>

      <Text fontSize="sm" color="fg.muted">
        This form is anonymous. Nothing is recorded about who answered it
        {form.closesOn === null ? "." : `, and it closes on ${form.closesOn}.`}
      </Text>

      <ReviewFormFields
        fields={form.formSchema.fields}
        values={values}
        disabled={submitting}
        onChange={(key, value) => setValues((previous) => ({ ...previous, [key]: value }))}
      />

      {error === null ? null : (
        <Text fontSize="sm" color="fg.error">{error}</Text>
      )}

      <Box>
        <Button
          colorPalette="blue"
          loading={submitting}
          disabled={missing.length > 0}
          onClick={() => void onSubmit()}
        >
          Send feedback
        </Button>
        {missing.length === 0 ? null : (
          <Text mt={2} fontSize="sm" color="fg.muted">
            Still to answer: {missing.join(", ")}
          </Text>
        )}
      </Box>
    </Shell>
  );
}

/** Today in Vienna, for deciding what to render. Never for deciding what to accept. */
function todayInVienna(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Vienna",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Center minH="100vh" bg="bg.subtle" px={4} py={10}>
      <Card.Root maxW="2xl" w="full" borderWidth="1px" borderColor="border" shadow="sm">
        <Card.Body gap={5}>{children}</Card.Body>
      </Card.Root>
    </Center>
  );
}
